// Player-data feedback loop — analytics / telemetry instrumentation.
//
// Architecture: every event flows through one `track(eventName, props)`
// call. Provider (PostHog) is plugged in once via `initAnalytics()` at
// app boot (the public project token is in main.tsx, since Lovable's build
// ignores repo .env files; an env variable overrides it). Events are sent only from
// the live domains (LIVE_HOSTS): dev servers, Lovable previews, local
// production builds and headless test runs short-circuit to a
// `console.debug` so they never pollute real data. Swapping providers (or
// self-hosting) is a single-file change here; call sites import only `track`.
//
// Privacy posture:
//   • No PII captured. The full property allow-list is enforced both
//     at send-time (`stripAnyPII` defence-in-depth) and at build-time
//     (`scripts/analytics-pii-audit.mjs` greps every track() call site).
//   • PostHog is configured cookie-free (persistence: 'localStorage') so
//     no GDPR cookie banner is required.
//   • Anonymous distinct_id only — never call posthog.identify().
//   • Session replay is hard-off; turning it on would require a much
//     stricter consent flow.
//
// Documented at /privacy in the app and in the §F PII-audit script.

import type { PostHog } from "posthog-js";

// posthog-js (~70 KB gzipped) is loaded on demand, after the page is up and
// only when a key is configured and the player has not opted out, so it is
// never part of the first-load download. Events tracked before it arrives
// are queued (capped) and sent once it has initialised.
let posthog: PostHog | null = null;
let initialized = false;
let enabled = false;
const pending: Array<[string, Record<string, unknown> | undefined]> = [];
const MAX_PENDING = 50;

/** The only hostnames that send analytics: real players on the live site. */
const LIVE_HOSTS = new Set(["chroniclesofeurope.com", "www.chroniclesofeurope.com"]);

/**
 * Property keys the analytics layer is allowed to send. Kept in sync with
 * scripts/analytics-pii-audit.mjs. Unknown keys are stripped before send.
 */
const ALLOWED_PROPERTIES = new Set<string>([
  // Identity / context
  "chronicle",
  "character",
  "is_resume",
  "ts",

  // Decision + ending
  "node_id",
  "choice_id",
  "turn",
  "chapter",
  "turns",
  "ending_flag",
  "post_statuses",
  "post_relationships",
  "flags_added",
  "total_flags",
  "final_statuses",
  "time_to_complete_sec",
  "session_duration_sec",
  "last_node_id",
  "last_turn",
  "prev_ending_flag",

  // Encounters
  "news_id",
  "batch_size",
  "setpiece_id",
  "figure",
  "actor_id",

  // Locked-choice mechanic
  "gates",

  // Client-error reports (src/lib/errorReporting.ts). The message is
  // scrubbed of emails, query strings, long numbers and token-like
  // strings before it gets here; path is location.pathname only.
  "error_kind",
  "error_message",
  "error_boundary",
  "path",
]);

function stripAnyPII(properties: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(properties)) {
    if (ALLOWED_PROPERTIES.has(k)) out[k] = v;
    else if (import.meta.env.DEV) console.warn(`[analytics] dropped unknown property "${k}"`);
  }
  return out;
}

export interface InitAnalyticsOpts {
  /** PostHog project key. If undefined / empty, analytics is disabled. */
  key?: string;
  /** PostHog API host. Defaults to EU cloud. */
  host?: string;
  /** Initial opt-out state from settings. Disables capture even if key is set. */
  optedOut: boolean;
}

export function initAnalytics(opts: InitAnalyticsOpts) {
  if (!opts.key) {
    if (import.meta.env.DEV) console.debug("[analytics] no key set, disabled");
    enabled = false;
    return;
  }
  if (opts.optedOut) {
    if (import.meta.env.DEV) console.debug("[analytics] user opted out, disabled");
    enabled = false;
    return;
  }
  if (!LIVE_HOSTS.has(window.location.hostname)) {
    console.debug(`[analytics] ${window.location.hostname} is not a live domain, disabled`);
    enabled = false;
    return;
  }
  enabled = true;
  const key = opts.key;
  const load = () => import("posthog-js")
    .then(({ default: ph }) => {
      ph.init(key, {
        api_host: opts.host ?? "https://eu.i.posthog.com",
        persistence: "localStorage",       // cookie-free
        autocapture: false,                // we hand-pick events
        capture_pageview: true,
        disable_session_recording: true,   // hard-off; needs stricter consent
        loaded: () => {
          if (import.meta.env.DEV) console.debug("[analytics] posthog ready");
        },
      });
      posthog = ph;
      initialized = true;
      // The player may have opted out while the library was loading.
      if (!enabled) ph.opt_out_capturing();
      for (const [event, props] of pending.splice(0)) if (enabled) ph.capture(event, props);
    })
    .catch(() => {
      // Offline, blocked by an ad blocker, or a stale deploy: analytics is
      // optional, so the game simply carries on without it.
      enabled = false;
      pending.length = 0;
    });
  // Fetch only once the page has loaded and the browser is idle, so the
  // library never competes with the first screen for bandwidth.
  const whenIdle = () => {
    const ric = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
    if (ric) ric(() => { void load(); }, { timeout: 5000 });
    else window.setTimeout(() => { void load(); }, 1500);
  };
  if (document.readyState === "complete") whenIdle();
  else window.addEventListener("load", whenIdle, { once: true });
}

/** Toggle capture without re-initialising. Wired to the settings opt-out toggle. */
export function setOptedOut(optedOut: boolean) {
  if (!initialized || !posthog) {
    // Not loaded (no key, opted out at boot, or still loading): only an
    // opt-out can take effect now; an opt-in without a loaded client stays off.
    if (optedOut) { enabled = false; pending.length = 0; }
    return;
  }
  if (optedOut) {
    posthog.opt_out_capturing();
    enabled = false;
  } else {
    posthog.opt_in_capturing();
    enabled = true;
  }
}

/**
 * Fire an analytics event. Always safe to call: in dev / preview / opted-
 * out / pre-init this is a no-op (with a debug log in dev). PII-allow-list
 * filtered before send; unknown keys dropped with a dev-only warning.
 */
export function track(event: string, properties?: Record<string, unknown>) {
  const cleaned = properties ? stripAnyPII(properties) : undefined;
  if (import.meta.env.DEV) console.debug("[analytics]", event, cleaned ?? "");
  if (!enabled) return;
  if (!posthog) {
    if (pending.length < MAX_PENDING) pending.push([event, cleaned]);
    return;
  }
  posthog.capture(event, cleaned);
}
